import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
    IMAGE_SIZE_ERROR_MESSAGE,
    RichTextEditor,
    isImageSizeError,
} from "../../../../components";
import { useFetch, useIsBasicRole, useSnackbar } from "../../../../hooks";

import { BACKEND_ROUTES } from "../../../../backendRoutes";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { DatePicker } from "@mui/x-date-pickers";
import { INPUT_VALIDATION } from "../../../../constants";
import { LoadingButton } from "@mui/lab";
import { formatDateISO } from "../../../../utils/formatTime";
import { parseISO } from "date-fns";
import { useState } from "react";

export const MilestoneForm = ({
    open,
    onClose,
    mutateTimeline,
    contractUuid,
    availableSteps,
    milestone,
}) => {
    const isBasicRole = useIsBasicRole();
    const { openSnackbar } = useSnackbar();

    const [dateError, setDateError] = useState();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const { post, patch, destroy } = useFetch();

    const {
        control,
        reset,
        formState: { errors, isSubmitting },
        handleSubmit,
    } = useForm({
        disabled: isBasicRole,
        defaultValues: milestone
            ? {
                  stepUuid: milestone.stepUuid,
                  date: parseISO(milestone.date),
                  report: milestone.report,
              }
            : {
                  stepUuid: "",
                  date: null,
                  report: null,
              },
    });

    const onSubmit = async (payload) => {
        const body = {
            ...payload,
            contractUuid,
            date: formatDateISO(payload.date),
            report: payload.report && JSON.stringify(payload.report),
        };
        let updatedMilestone;
        try {
            updatedMilestone = await (milestone
                ? patch(`${BACKEND_ROUTES.MILESTONE}/${milestone.uuid}`, {
                      body,
                      forwardError: true,
                  })
                : post(BACKEND_ROUTES.MILESTONE, {
                      body,
                      forwardError: true,
                  }));
        } catch (error) {
            openSnackbar(
                isImageSizeError(error)
                    ? IMAGE_SIZE_ERROR_MESSAGE
                    : error.message,
                "error"
            );
            return;
        }

        if (updatedMilestone) {
            mutateTimeline();
            openSnackbar(
                `Milestone ${updatedMilestone.date} ${milestone ? "updated" : "created"} successfully.`,
                "success"
            );
        }
        onClose();
    };
    const onCancel = () => {
        reset();
        onClose();
    };
    const onConfirmDelete = async () => {
        onClose();
        const deleteResult = await destroy(
            `${BACKEND_ROUTES.MILESTONE}/${milestone.uuid}`
        );
        if (deleteResult) {
            mutateTimeline();
            openSnackbar(
                `Milestone ${milestone.date} deleted successfully.`,
                "success"
            );
        }
    };

    const requiredLabel = !milestone ? " *" : "";

    return (
        <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
            <DialogTitle>
                {milestone ? "Update the milestone" : "Create a new milestone"}
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit(onSubmit)} id="milestoneForm">
                    <Grid container spacing={1} alignItems="center">
                        <Grid size={6}>
                            <Controller
                                control={control}
                                name="stepUuid"
                                render={({ field: { ref, ...fieldProps } }) => (
                                    <TextField
                                        {...fieldProps}
                                        fullWidth
                                        id="stepUuid"
                                        label={`Step${requiredLabel}`}
                                        select
                                        inputRef={ref}
                                        error={Boolean(errors.stepUuid)}
                                        helperText={errors.stepUuid?.message}
                                        disabled={Boolean(milestone)}
                                    >
                                        {milestone ? (
                                            <MenuItem
                                                key={milestone.stepUuid}
                                                value={milestone.stepUuid}
                                            >
                                                {milestone.Step.label}
                                            </MenuItem>
                                        ) : (
                                            availableSteps.map((step) => (
                                                <MenuItem
                                                    key={step.uuid}
                                                    value={step.uuid}
                                                >
                                                    {step.label}
                                                </MenuItem>
                                            ))
                                        )}
                                    </TextField>
                                )}
                                rules={{
                                    required: INPUT_VALIDATION.REQUIRED,
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Controller
                                name="date"
                                control={control}
                                render={({
                                    field: { ref, onChange, ...fieldProps },
                                }) => (
                                    <DatePicker
                                        {...fieldProps}
                                        slotProps={{
                                            field: { clearable: true },
                                            textField: {
                                                fullWidth: true,
                                                error: Boolean(
                                                    errors.date || dateError
                                                ),
                                                helperText:
                                                    errors.date?.message ||
                                                    dateError,
                                            },
                                        }}
                                        id="date"
                                        label={`Date${requiredLabel}`}
                                        format="yyyy-MM-dd"
                                        onChange={onChange}
                                        inputRef={ref}
                                        onError={(reason) => {
                                            switch (reason) {
                                                case "invalidDate":
                                                    setDateError(
                                                        INPUT_VALIDATION.INVALID_DATE
                                                    );
                                                    break;
                                                default:
                                                    setDateError(undefined);
                                            }
                                        }}
                                        sx={{ my: 1 }}
                                    />
                                )}
                                rules={{
                                    required: INPUT_VALIDATION.REQUIRED,
                                    validate: () => !dateError,
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Controller
                        name="report"
                        control={control}
                        render={({ field }) => (
                            <RichTextEditor
                                defaultEditorState={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                {milestone && (
                    <>
                        <Button
                            onClick={() => setConfirmModalOpen(true)}
                            variant="contained"
                            color="error"
                            sx={{ ml: 2 }}
                        >
                            Delete
                        </Button>
                        <Box sx={{ flexGrow: 1 }} />
                    </>
                )}
                <Button onClick={onCancel}>Cancel</Button>
                <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    form="milestoneForm"
                >
                    {milestone ? "Update" : "Add"}
                </LoadingButton>
            </DialogActions>
            <ConfirmModal
                isOpen={confirmModalOpen}
                onCancel={() => setConfirmModalOpen(false)}
                onConfirm={onConfirmDelete}
                title="Are you sure you want to delete the milestone?"
                contentText={<>This step will be deleted.</>}
            />
        </Dialog>
    );
};
